import {
  DefaultError,
  InfiniteData,
  QueryFunction,
  QueryKey,
  QueryStatus,
  UndefinedInitialDataInfiniteOptions,
  useInfiniteQuery as useReactQueryInfinite,
  useMutation as useReactQueryMutation,
  UseMutationOptions,
  useQuery as useReactQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useMemo } from 'react';

export type { QueryStatus } from '@tanstack/react-query';
export {
  QueryClient,
  QueryClientProvider,
  useQueries,
} from '@tanstack/react-query';

import { queryClient } from '@/queryclient';
import { JobStatus } from '@/types';
import { createError } from '@/utils/error.util';
import { capitalize } from '@/utils/lodash.util';
import { appendPeriodIfMissing } from '@/utils/text.util';

export function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> &
    CustomOptions<TVariables> &
    Required<
      Pick<
        UseMutationOptions<TData, TError, TVariables, TContext>,
        'mutationFn'
      >
    >
) {
  return useReactQueryMutation({
    ...options,
    mutationFn: async (...args) => {
      try {
        return await options.mutationFn(...args);
      } catch (error) {
        throw getQueryError(options, error, args?.[0]);
      }
    },
  });
}

export function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> &
    CustomOptions<void> & {
      queryFn: QueryFunction<TQueryFnData, TQueryKey, never>;
    }
) {
  const query = useReactQuery({
    ...options,
    queryFn: async (...args) => {
      try {
        return await options.queryFn(...args);
      } catch (error) {
        throw getQueryError(options, error, undefined);
      }
    },
  });

  return {
    ...query,
    updateAllSubQueries: (data: (prevData: TData) => TData) => {
      updateAllQueryCache<TData>(options.queryKey, data);
    },
    invalidate: () => {
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey.length >= options.queryKey.length &&
          options.queryKey.every((key, index) => query.queryKey[index] === key),
      });
    },
  };
}

export function useInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<InfinityData<TQueryFnData>>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UndefinedInitialDataInfiniteOptions<
    InfinityData<TQueryFnData>,
    TError,
    TData,
    TQueryKey,
    TPageParam
  > &
    CustomOptions<void> & {
      queryFn: QueryFunction<InfinityData<TQueryFnData>, TQueryKey, TPageParam>;
    }
) {
  const query = useReactQueryInfinite({
    ...options,
    queryFn: async (...args) => {
      try {
        return await options.queryFn(...args);
      } catch (error) {
        throw getQueryError(options, error, undefined);
      }
    },
  });

  return {
    ...query,
    updateAllSubQueries: (
      data: (
        currentValue: InfiniteData<InfinityData<TQueryFnData>>
      ) => InfiniteData<InfinityData<TQueryFnData>>
    ) => {
      updateAllQueryCache(options.queryKey, data);
    },
    invalidate: () => {
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey.length >= options.queryKey.length &&
          options.queryKey.every((key, index) => query.queryKey[index] === key),
      });
    },
  };
}

interface InfinityData<TData> {
  count?: number;
  data: TData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  offset: any;
}

export type UseExtendInfiniteQueryOptions<TData> = Omit<
  UndefinedInitialDataInfiniteOptions<InfinityData<TData>, Error>,
  'queryKey' | 'getNextPageParam' | 'initialPageParam'
> & {
  email?: string;
};

export type UseExtendQueryOptions<TData> = Omit<
  UseQueryOptions<TData, Error>,
  'queryFn' | 'queryKey'
> & {
  doNotImpersonate?: boolean;
  email?: string;
};

function updateAllQueryCache<T>(key: QueryKey, data: (currentValue: T) => T) {
  const queryCache = queryClient.getQueryCache();

  queryCache.findAll({ queryKey: key, type: 'active' }).forEach(cache => {
    queryClient.setQueryData(cache.queryKey, data);
  });
}

function getQueryError<TVariable>(
  options: CustomOptions<TVariable>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
  variable: TVariable
) {
  const isLicenseError = error?.response?.status === 402;
  const errorFromResponse = options.getErrorFromResponse
    ? error.response?.data
    : '';
  const errorMessageByStatusCode =
    options.errorByStatusCode &&
    (typeof options.errorByStatusCode === 'function'
      ? options.errorByStatusCode(variable)[error?.response?.status]
      : options.errorByStatusCode[error?.response?.status]);

  const defaultError =
    typeof options.defaultErrorMessage === 'function'
      ? options.defaultErrorMessage(variable)
      : options.defaultErrorMessage;

  const queryErrorTitle =
    errorFromResponse || errorMessageByStatusCode || defaultError;
  let queryErrorMessage = '';

  if (isLicenseError && !errorMessageByStatusCode) {
    queryErrorMessage = appendContactSupport('License Required');
  } else {
    queryErrorMessage = appendContactSupport('');
  }

  return createError(queryErrorMessage, queryErrorTitle);
}

function appendContactSupport(error: string) {
  const support = 'Please contact support@praetorian.com.';

  return capitalize(
    error ? appendPeriodIfMissing(error) + ` ${support}` : support
  );
}

interface CustomOptions<TVariables> {
  defaultErrorMessage: string | ((variable: TVariables) => string);
  errorByStatusCode?:
    | Record<number, string>
    | ((variable: TVariables) => Record<number, string>);
  getErrorFromResponse?: boolean;
}

export function mergeJobStatus(
  jobStatus: (JobStatus | undefined)[]
): JobStatus | undefined {
  if (jobStatus.includes(JobStatus.Running)) {
    return JobStatus.Running;
  }
  if (jobStatus.includes(JobStatus.Queued)) {
    return JobStatus.Queued;
  }
  if (jobStatus.includes(JobStatus.Fail)) {
    return JobStatus.Fail;
  }
  if (jobStatus.includes(JobStatus.Pass)) {
    return JobStatus.Pass;
  }
  return undefined;
}

/**
 * This util combines status from multiple queries into one status
 * @param statusList List of QueryStatus
 * @returns
 */
export function mergeStatus(...statusList: QueryStatus[]): QueryStatus {
  let isError = false;
  let isLoading = false;

  statusList.forEach(s => {
    if (s === 'error') {
      isError = true;
    }
    if (s === 'pending') {
      isLoading = true;
    }
  });

  if (isError) {
    return 'error';
  } else if (isLoading) {
    return 'pending';
  }

  return 'success';
}

export function useMergeStatus(...statusList: QueryStatus[]): QueryStatus {
  return useMemo(
    () => mergeStatus(...statusList),
    [JSON.stringify(statusList)]
  );
}
